import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { pathLabelOf, shortUuid, type Container } from "@egalregal/shared";
import { useAppStore } from "../../shared/store/app";
import { useT, type Translate } from "../../shared/i18n";
import { LocationDotIcon } from "../../shared/components/icons";
import { PickerDialog } from "./PickerDialog";

/** The current-location picker offers every container in the entry. */
const NO_EXCLUSIONS = new Set<string>();

/**
 * Quick-add header for the create form (ticket 12): sets the transient
 * "current location" under which every following create is linked, and offers
 * a one-click stop. The location is client state only (ADR-0003/0030), so the
 * bar never writes or reads the catalog; the create form supplies the parent.
 *
 * @param overrideParentLabel the one-off parent that wins over the current
 * location for this create (an explicit `?parent`), so the bar does not claim
 * the box will land under the location.
 */
export function QuickAddBar({ overrideParentLabel }: { overrideParentLabel?: string }) {
  const t = useT();
  const currentLocationId = useAppStore((s) => s.currentLocationId);
  const setCurrentLocation = useAppStore((s) => s.setCurrentLocation);
  const containers = useAppStore((s) => s.containers);
  const [picking, setPicking] = useState(false);

  const label =
    currentLocationId === undefined ? undefined : locationLabel(containers, currentLocationId);
  const message = messageFor(t, label, overrideParentLabel);

  return (
    <Box
      component="section"
      aria-label={t("quickAddTitle")}
      data-testid="quick-add-bar"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
        p: 1.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "action.hover",
      }}
    >
      <LocationDotIcon fontSize="small" color="action" />
      <Typography
        variant="body2"
        sx={{ flexGrow: 1, minWidth: 0 }}
        color="text.secondary"
        aria-live="polite"
      >
        {message}
      </Typography>
      {label === undefined ? (
        <Button size="small" variant="outlined" onClick={() => setPicking(true)}>
          {t("quickAddSet")}
        </Button>
      ) : (
        <>
          <Button size="small" onClick={() => setPicking(true)}>
            {t("quickAddChange")}
          </Button>
          <Button size="small" color="inherit" onClick={() => setCurrentLocation(undefined)}>
            {t("quickAddStop")}
          </Button>
        </>
      )}
      <PickerDialog
        open={picking}
        title={t("quickAddPickTitle")}
        containers={containers}
        excludeIds={NO_EXCLUSIONS}
        onPick={(id) => setCurrentLocation(id)}
        onClose={() => setPicking(false)}
      />
    </Box>
  );
}

/**
 * The bar's status line. Without a location it explains the mode; with one it
 * names the location, or names the one-off parent that overrides it so the
 * copy never claims a box will land somewhere it will not.
 *
 * @param t the language accessor.
 * @param label the current location path, or undefined when unset.
 * @param overrideParentLabel the explicit parent winning this create, if any.
 * @returns the translated status line.
 */
function messageFor(
  t: Translate,
  label: string | undefined,
  overrideParentLabel: string | undefined,
): string {
  if (label === undefined) return t("quickAddHint");
  if (overrideParentLabel !== undefined) {
    return t("quickAddOverride", { parent: overrideParentLabel, path: label });
  }
  return t("quickAddUnder", { path: label });
}

/**
 * The location's full path when the entry still holds it, otherwise its short
 * id — a stale target must stay identifiable instead of reading as unset.
 */
function locationLabel(containers: readonly Container[], id: string): string {
  return containers.some((container) => container.id === id)
    ? pathLabelOf(containers, id)
    : shortUuid(id);
}
